import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { api, errMsg } from "@/lib/api";
import { useAuth, nextPath } from "@/lib/auth";
import { useSeo } from "@/lib/useSeo";

function Shell({ title, sub, children }) {
  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
      <div className="px-5 h-16 flex items-center"><Link to="/" data-testid="auth-home-link"><Logo /></Link></div>
      <div className="flex-1 grid place-items-center px-5 pb-16">
        <div className="w-full max-w-md rise">
          <h1 className="font-heading text-4xl font-extrabold">{title}</h1>
          {sub && <p className="text-slate-600 mt-2 mb-6">{sub}</p>}
          <div className="pl-card p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} data-testid="google-signin-btn" className="pl-btn-ghost w-full">
      <svg className="h-5 w-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
      Continue with Google
    </button>
  );
}

const Divider = () => <div className="flex items-center gap-3 my-5 text-xs text-slate-400"><span className="h-px flex-1 bg-[#EFECE6]" />or<span className="h-px flex-1 bg-[#EFECE6]" /></div>;

function AuthForm({ mode }) {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { setUser } = useAuth();
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/auth/${mode}`, mode === "login" ? { email: f.email, password: f.password } : f);
      setUser(data);
      nav(nextPath(data), { replace: true });
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };
  const inp = (k, label, type = "text", ac) => (
    <div><label className="pl-label">{label}</label>
      <input required type={type} autoComplete={ac} data-testid={`${mode}-${k}-input`} className="pl-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} minLength={k === "password" && mode === "register" ? 8 : undefined} /></div>
  );
  return (
    <>
      <GoogleButton />
      <Divider />
      <form onSubmit={submit} className="space-y-4">
        {mode === "register" && inp("name", "Your name", "text", "name")}
        {inp("email", "Email", "email", "email")}
        {inp("password", mode === "register" ? "Password (8+ characters)" : "Password", "password", mode === "register" ? "new-password" : "current-password")}
        {err && <p data-testid={`${mode}-error`} className="text-sm text-[#C2410C] font-semibold">{err}</p>}
        <button data-testid={`${mode}-submit-btn`} disabled={busy} className="pl-btn-primary w-full">{busy ? "One sec..." : mode === "login" ? "Log in" : "Start free trial"}</button>
      </form>
    </>
  );
}

export function LoginPage() {
  useSeo("Log in");
  return (
    <Shell title="Welcome back" sub="Log in to see today's checklist.">
      <AuthForm mode="login" />
      <div className="mt-5 flex justify-between text-sm">
        <Link to="/forgot" className="text-slate-500" data-testid="forgot-link">Forgot password?</Link>
        <Link to="/signup" className="font-semibold text-[#D97706]" data-testid="to-signup-link">Create account</Link>
      </div>
    </Shell>
  );
}

export function SignupPage() {
  useSeo("Start your free trial");
  return (
    <Shell title="Start your free trial" sub="14 days free. No credit card. Unlimited pets.">
      <AuthForm mode="register" />
      <p className="mt-5 text-xs text-slate-500">By signing up you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy policy</Link>.</p>
      <p className="mt-3 text-sm">Already have an account? <Link to="/login" className="font-semibold text-[#D97706]" data-testid="to-login-link">Log in</Link></p>
    </Shell>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Reset password" sub="We'll email you a link that works for 1 hour.">
      {sent ? <p data-testid="forgot-sent-msg">If an account exists for {email}, a reset link is on its way.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <input required type="email" className="pl-input" placeholder="you@email.com" data-testid="forgot-email-input" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button className="pl-btn-primary w-full" data-testid="forgot-submit-btn">Send reset link</button>
        </form>)}
    </Shell>
  );
}

export function ResetPage() {
  const { token } = useParams();
  const [pw, setPw] = useState("");
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token, password: pw }); toast.success("Password updated. Please log in."); nav("/login"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        <input required minLength={8} type="password" className="pl-input" placeholder="New password (8+ characters)" data-testid="reset-password-input" value={pw} onChange={(e) => setPw(e.target.value)} />
        <button className="pl-btn-primary w-full" data-testid="reset-submit-btn">Update password</button>
      </form>
    </Shell>
  );
}

export function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", { session_id: sid })
      .then(({ data }) => { setUser(data); window.history.replaceState(null, "", window.location.pathname); nav(nextPath(data), { replace: true }); })
      .catch(() => { toast.error("Google sign-in failed. Please try again."); nav("/login", { replace: true }); });
  }, [location.hash, nav, setUser]);
  return null;
}
